/* eslint-disable react/require-default-props, react/forbid-prop-types, prettier/prettier */
import React, { useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronRight,
  ChevronDown,
  PlusCircle,
  Code,
  Users,
  Briefcase,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import GoalsConstants from '../../../../constants/goals.constants';
import reviewConstants from '../../../../constants/review.constants';
import UserContext from '../../../../hooks/context/UserContext';
import { cn } from '../../../../lib/utils';
import { mergeSimilarSkillDetails } from '../../../Head/helpers/reviews.helpers';

function SkillTreeNode({ node, skillsData, isDisabled, reviewData, expanded, onToggle, depth = 0 }) {
  const { socketInstance } = useContext(UserContext);

  const nodeChildren = useMemo(() => {
    if (!node.children?.length && node.details) {
      return mergeSimilarSkillDetails(node.details);
    }
    return skillsData.filter((item) => node.children?.includes(item.itemId));
  }, [node, skillsData]);

  const hasChildren = nodeChildren.length > 0;
  const isExpanded = expanded.includes(node.itemId);
  const isOnHold = node.status === GoalsConstants.GOAL_STATUSES.ON_HOLD;
  const isInProgress = node.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS;
  const isDone = node.status === GoalsConstants.GOAL_STATUSES.DONE;
  const isApproved = node.status === GoalsConstants.GOAL_STATUSES.APPROVED;
  const isDeclined = node.status === GoalsConstants.GOAL_STATUSES.DECLINED;
  const isReviewProcessed = reviewData?.status === reviewConstants.REVIEW_STATUSES.PROCESSED;

  const handleAddSkill = (event) => {
    event.stopPropagation();

    if (node.isDetails || isDisabled) {
      return;
    }

    socketInstance?.emit('send-goal-status', {
      itemId: node.itemId,
      status: GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
      skillType: node.skillType,
    });
  };

  const getStatusIcon = () => {
    if (isOnHold) {
      return (
        <button
          type="button"
          onClick={handleAddSkill}
          className="p-0.5 hover:bg-primary/10 rounded transition-colors"
          title="Add to progress"
        >
          <PlusCircle className="h-4 w-4 text-primary" />
        </button>
      );
    }
    if (isInProgress) {
      return <Clock className="h-4 w-4 text-amber-500" title="In Progress" />;
    }
    if (isDone && !isReviewProcessed) {
      return <CheckCircle2 className="h-4 w-4 text-amber-500" title="Ready for review" />;
    }
    if (isReviewProcessed) {
      return <CheckCircle2 className="h-4 w-4 text-amber-500" title="Ready for review" />;
    }
    if (isApproved && !isReviewProcessed) {
      return <CheckCircle2 className="h-4 w-4 text-green-500" title="Approved" />;
    }
    if (isDeclined && !node.isDetails && !isReviewProcessed) {
      return (
        <button
          type="button"
          onClick={handleAddSkill}
          className="p-0.5 hover:bg-primary/10 rounded transition-colors"
          title="Add to progress (Declined)"
        >
          <PlusCircle className="h-4 w-4 text-amber-500" />
        </button>
      );
    }
    if (isDeclined && node.isDetails && !isReviewProcessed) {
      return <XCircle className="h-4 w-4 text-red-500" title="Declined" />;
    }
    return null;
  };

  return (
    <div className="select-none">
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-2 py-1.5 px-2 text-left rounded-md transition-colors',
          'hover:bg-muted/50',
          !isOnHold && !isInProgress && !isDone && !isApproved && 'text-muted-foreground',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onToggle(node.itemId)}
      >
        {hasChildren ? (
          <span className="shrink-0 w-4 h-4 flex items-center justify-center">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        ) : (
          <span className="shrink-0 w-4" />
        )}
        <span className="flex-1 text-base truncate">{node.name}</span>
        {getStatusIcon()}
        {node.recommendation && isApproved && (
          <AlertCircle className="h-4 w-4 text-muted-foreground" title={node.recommendation} />
        )}
        {node.comment && isDeclined && node.isDetails && (
          <AlertCircle className="h-4 w-4 text-muted-foreground" title={node.comment} />
        )}
      </button>

      {isExpanded && hasChildren && (
        <div className="ml-2 border-l border-border">
          {nodeChildren.map((child) => (
            <SkillTreeNode
              key={child.itemId}
              node={{
                isDetails: !node.children?.length,
                details: [],
                children: [],
                skillType: node.skillType,
                ...child,
              }}
              skillsData={skillsData}
              isDisabled={isDisabled}
              reviewData={reviewData}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

SkillTreeNode.propTypes = {
  node: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.array,
    skillType: PropTypes.string,
    status: PropTypes.string,
    isDetails: PropTypes.bool,
    recommendation: PropTypes.string,
    comment: PropTypes.string,
  }).isRequired,
  skillsData: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool,
  reviewData: PropTypes.shape({
    status: PropTypes.string,
  }),
  expanded: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  depth: PropTypes.number,
};

function SkillTreePanel({
  hardSkills,
  softSkills,
  personalSkills,
  isDisabled,
  reviewData,
}) {
  const [activeTab, setActiveTab] = useState('hard');

  const skillsMap = {
    hard: hardSkills,
    soft: softSkills,
    personal: personalSkills,
  };

  const currentSkills = skillsMap[activeTab] || [];

  // Get root nodes (nodes that are not children of any other node)
  const rootNodes = useMemo(() => {
    return currentSkills.filter(
      (item) => !currentSkills.some((skill) => skill.children?.includes(item.itemId)),
    );
  }, [currentSkills]);

  // Get parent nodes (nodes that have children) - these should be expanded by default
  const parentNodeIds = useMemo(() => {
    return currentSkills
      .filter((skill) => skill.children?.length > 0)
      .map((s) => s.itemId);
  }, [currentSkills]);

  // Expanded state - initialized with parent nodes expanded
  const [expanded, setExpanded] = useState(parentNodeIds);

  // Update expanded state when switching tabs
  React.useEffect(() => {
    setExpanded(parentNodeIds);
  }, [parentNodeIds]);

  const handleToggle = (itemId) => {
    setExpanded((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  const getSkillCount = (skills) => {
    let count = 0;
    skills.forEach((skill) => {
      if (skill.status === GoalsConstants.GOAL_STATUSES.ON_HOLD
        || skill.status === GoalsConstants.GOAL_STATUSES.DECLINED) {
        if (skill.details?.length) {
          count += skill.details.length;
        }
      }
    });
    return count;
  };

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="pb-3 shrink-0">
        <CardTitle>Skill Tree</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          <TabsList className="grid w-full grid-cols-3 shrink-0">
            <TabsTrigger value="hard" className="text-xs sm:text-sm">
              <Code className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Hard</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {getSkillCount(hardSkills)}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="soft" className="text-xs sm:text-sm">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Soft</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {getSkillCount(softSkills)}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="personal" className="text-xs sm:text-sm">
              <Briefcase className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Personal</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {getSkillCount(personalSkills)}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              {rootNodes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Code className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No {activeTab} skills available yet.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {rootNodes.map((node) => (
                    <SkillTreeNode
                      key={node.itemId}
                      node={node}
                      skillsData={currentSkills}
                      isDisabled={isDisabled}
                      reviewData={reviewData}
                      expanded={expanded}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

SkillTreePanel.propTypes = {
  hardSkills: PropTypes.array,
  softSkills: PropTypes.array,
  personalSkills: PropTypes.array,
  isDisabled: PropTypes.bool,
  reviewData: PropTypes.shape({
    status: PropTypes.string,
  }),
};

SkillTreePanel.defaultProps = {
  hardSkills: [],
  softSkills: [],
  personalSkills: [],
  isDisabled: false,
  reviewData: null,
};

export default SkillTreePanel;
